/**
 * PDP integrations & polish
 * -------------------------
 * Consolidates the five inline <script> blocks that previously lived in
 * snippets/product-template.liquid (Swatch King relabelling, size label,
 * fit badge, add-to-cart guard, desktop sticky offset). Loaded product-only
 * from layout/theme.liquid with defer, BEFORE theme.js — deferred scripts
 * execute in document order, so everything here still binds ahead of the
 * theme's variant engine.
 *
 * Each module is a singleton IIFE with a window.__pdp*Bound guard (the same
 * guards the inline versions used) so a re-render can never double-bind.
 * There is one PDP section per page; modules resolve it lazily via
 * getProductSection()/getMobileRoot() so they keep working after the
 * sibling-product swap replaces the section's DOM.
 */
(function () {
  'use strict';

  function getProductSection() {
    return document.querySelector('.product-section[data-section-type="product"]');
  }

  function getMobileRoot() {
    return document.querySelector('.product-section[data-pdp-mobile-root]') || document;
  }

  /* ── Module 1: Swatch King relabelling + Kiwi size-guide relocation ──
     Rewrites third-party Swatch King labels to "OPTION: VALUE", strips the
     invalid role attr from its <style> tags, injects the "WHAT'S MY SIZE?"
     button and relocates the Kiwi app-block trigger next to the size label.
     Swatch King renders asynchronously, so this polls briefly and then
     follows a MutationObserver. */
  (function () {
    if (window.__pdpSwatchRelabelBound) return;
    window.__pdpSwatchRelabelBound = true;

    var rewriteTimer = null;
    var observer = null;

    function scheduleRewrite(delay) {
      clearTimeout(rewriteTimer);
      rewriteTimer = setTimeout(rewriteSwatchLabels, delay || 0);
    }

    // Only ever relocate/relabel a node that is verifiably Kiwi's trigger.
    // After a sibling swap the fresh app-block container does NOT hold the
    // trigger (Kiwi injects on full page load only; theme.js preserves the
    // live node across swaps) — an unverified "first anchor in the host"
    // grab dressed a terms-and-conditions link up as "What's my size?".
    function isKiwiTrigger(el) {
      if (!el) return false;
      var cls = (typeof el.className === 'string' && el.className) || '';
      if (/_ks_|kiwi/i.test(cls)) return true;
      if (el.querySelector && el.querySelector('[class*="_ks_"]')) return true;
      // Text check as last resort — but never accept a link that navigates
      // somewhere real: Kiwi's trigger opens a modal, it doesn't route.
      var href = el.getAttribute && (el.getAttribute('href') || '');
      if (href && href !== '#' && href.indexOf('javascript:') !== 0) return false;
      return /what.?s my size|size (guide|chart)/i.test(el.textContent || '');
    }

    function findKiwiTrigger(root) {
      // Already relocated (by us, or re-rendered by Kiwi after a sibling
      // swap) — find it wherever it currently lives.
      var relocated = document.querySelector('.swatch-size-chart .ks-calculator-modal-link, .swatch-size-chart a, .swatch-size-chart button, .swatch-size-chart [role="button"]');
      if (isKiwiTrigger(relocated)) return relocated;
      // Kiwi renders its trigger as a SPAN (.ks-calculator-modal-link) at its
      // configured position, usually OUTSIDE the app-block host — an a/button
      // scan of the host alone never matches it.
      var rendered = root.querySelector('.ks-calculator-modal-link') || document.querySelector('.ks-calculator-modal-link');
      if (isKiwiTrigger(rendered)) return rendered;
      var host = root.querySelector('.ks-container-app-block') || document.querySelector('.ks-container-app-block');
      if (!host) return null;
      var candidate = host.querySelector('a, button, [role="button"]');
      return isKiwiTrigger(candidate) ? candidate : null;
    }

    function moveKiwiSizeGuide() {
      var root = getMobileRoot();
      var sizeContainer = root.querySelector('[option-name="Size"]');
      if (!sizeContainer) return false;

      var labelRow = sizeContainer.querySelector('.swatch-label-container');
      if (!labelRow) return false;

      if (!labelRow.classList.contains('size-chart-right')) {
        labelRow.classList.add('size-chart-right');
      }

      // Not found / not verifiable yet → return false so the poll/observer
      // retries once Kiwi injects or theme.js restores the preserved node.
      var kiwiTrigger = findKiwiTrigger(root);
      if (!kiwiTrigger) return false;

      var existingMount = labelRow.querySelector('.swatch-size-chart');
      var mount = existingMount || document.createElement('div');
      if (!existingMount) {
        mount.className = 'swatch-size-chart';
        labelRow.appendChild(mount);
      }

      if (kiwiTrigger.parentNode !== mount) {
        mount.appendChild(kiwiTrigger);
      }

      // Equality-guarded: an unconditional textContent write replaces the
      // text node even when identical, and that childList mutation re-fires
      // the observer below — a self-sustaining rewrite loop.
      if (kiwiTrigger.textContent !== "What's my size?") {
        kiwiTrigger.textContent = "What's my size?";
      }
      if (kiwiTrigger.getAttribute('aria-label') !== "What's my size?") {
        kiwiTrigger.setAttribute('aria-label', "What's my size?");
      }
      // CSS keys off this to hide the emptied host block.
      var kiwiHost = root.querySelector('.ks-container-app-block') || document.querySelector('.ks-container-app-block');
      if (kiwiHost && kiwiHost.getAttribute('data-kiwi-relocated') !== 'true') {
        kiwiHost.setAttribute('data-kiwi-relocated', 'true');
      }
      return true;
    }

    function rewriteSwatchLabels() {
      var root = getMobileRoot();

      document.querySelectorAll('style[app="vsk"][role]').forEach(function (styleEl) {
        styleEl.removeAttribute('role');
      });

      var containers = root.querySelectorAll('[option-name], .swatch-single');
      if (!containers.length) return false;
      var anyDone = false;
      containers.forEach(function (container) {
        var optName = (container.getAttribute('option-name') || '').toUpperCase();
        // Strip trailing ": value" if the attribute already includes it (e.g. "Size: M")
        optName = optName.replace(/:.*$/, '').trim();
        if (!optName) {
          // Swatch King no longer outputs option-name; infer from swatch type
          var hasImages = container.querySelector('.swatch-view-image');
          if (hasImages) {
            optName = 'COLOR';
          } else {
            var legend = container.querySelector('legend .swatch-option-name');
            if (legend) {
              var raw = legend.textContent.replace(/\s+/g, ' ').trim();
              if (raw && raw !== 'Selected option') {
                optName = raw.toUpperCase().replace(/:.*$/, '').trim();
              }
            }
          }
        }
        if (!optName) return;
        container.classList.toggle('pdp-option-size', optName === 'SIZE');
        container.classList.toggle('pdp-option-length', optName === 'LENGTH');

        container.querySelectorAll('.swatch-view-item[role="radio"]').forEach(function (item) {
          var visibleLabel = (item.textContent || '').replace(/\s+/g, ' ').trim();
          if (visibleLabel) {
            item.setAttribute('aria-label', visibleLabel);
          }
        });

        var nameEls = container.querySelectorAll('.swatch-option-name');
        // Find selected: prefer .swatch-selected, fall back to aria-checked
        var selected = container.querySelector('.swatch-selected') ||
          container.querySelector('[aria-checked="true"] .swatch-group-selector') ||
          container.querySelector('[aria-checked="true"]');
        if (!nameEls.length || !selected) return;
        var selectedText = selected.querySelector('.swatch-button-title-text');
        var val =
          selected.getAttribute('data-value') ||
          selected.getAttribute('orig-value') ||
          (selectedText ? selectedText.textContent : '') ||
          selected.getAttribute('aria-label') ||
          '';
        val = val.replace(/\s+/g, ' ').trim();
        if (!val) return;
        var html =
          '<span class="swatch-label-prefix">' + optName + ': </span>' +
          '<span class="swatch-label-value">' + val.toUpperCase() + '</span>';
        nameEls.forEach(function (nameEl) {
          if (nameEl.innerHTML !== html) {
            nameEl.innerHTML = html;
          }
          // Guarded: 'class' is in the observer's attributeFilter, and
          // classList.add re-sets the attribute even when already present.
          if (!nameEl.classList.contains('swatch-label-ready')) {
            nameEl.classList.add('swatch-label-ready');
          }
        });

        // Inject SIZE GUIDE button for size swatches
        if (optName === 'SIZE') {
          var labelRow = container.querySelector('.swatch-label-container') || container.querySelector('legend');
          if (labelRow) {
            labelRow.style.display = 'flex';
            labelRow.style.justifyContent = 'space-between';
            labelRow.style.alignItems = 'center';
            var btn = labelRow.querySelector('.swatch-size-guide-btn') || document.createElement('button');
            if (!btn.parentNode) {
              btn.type = 'button';
              btn.className = 'swatch-size-guide-btn';
              labelRow.appendChild(btn);
            }
            if (btn.textContent !== "WHAT'S MY SIZE?") {
              btn.textContent = "WHAT'S MY SIZE?";
            }
            var panel = root.querySelector('[data-pdp-panel][id*="PdpMobileDetails"]') || document.querySelector('[data-pdp-panel][id*="PdpMobileDetails"]');
            if (panel) {
              btn.setAttribute('data-pdp-panel-open', '');
              btn.setAttribute('data-pdp-panel-id', panel.id);
              btn.setAttribute('data-pdp-tab', 'model-size');
              btn.setAttribute('aria-controls', panel.id);
              btn.setAttribute('aria-haspopup', 'dialog');
            }
          }

          // Fit text now rendered in the features section of the details panel.
          // Remove any previously injected swatch fit text.
          var swatchesRoot = container.closest('.swatches');
          if (swatchesRoot) {
            swatchesRoot.querySelectorAll('.swatch-fit-text').forEach(function (el) { el.remove(); });
          }
        }

        anyDone = true;
      });
      return moveKiwiSizeGuide() || anyDone;
    }

    function attachObserver() {
      if (observer) observer.disconnect();
      var root = getMobileRoot();
      // Scope to the tightest container that exists: the Swatch King host,
      // else the product section. NEVER fall back to observing the whole
      // document — with subtree class-attribute observation that meant every
      // class flip anywhere on the page (carousels, drawers, hovers) ran the
      // relabel for the rest of the session. If neither container exists yet
      // (Swatch King renders async), retry shortly; the startup poll keeps
      // labels correct in the interim.
      var swatchRoot = null;
      if (root.querySelector) {
        swatchRoot = root.querySelector('variant-swatch-king');
      }
      if (!swatchRoot && root !== document) swatchRoot = root;
      if (!swatchRoot) swatchRoot = getProductSection();
      if (!swatchRoot) {
        setTimeout(attachObserver, 500);
        return;
      }
      observer = new MutationObserver(function () {
        scheduleRewrite(0);
      });
      observer.observe(swatchRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-checked'] });
    }

    var startupPoll = setInterval(function () {
      if (rewriteSwatchLabels()) {
        clearInterval(startupPoll);
      }
    }, 50);
    setTimeout(function () {
      clearInterval(startupPoll);
      rewriteSwatchLabels();
    }, 10000);

    attachObserver();

    // The sibling-product swap replaces the whole section, so rebind the
    // observer against the fresh nodes (the inline predecessor of this module
    // silently stopped relabelling after a swap).
    document.addEventListener('dfyne:sibling-product:loaded', function () {
      attachObserver();
      scheduleRewrite(0);
    });
    document.addEventListener('shopify:section:load', function () {
      attachObserver();
      scheduleRewrite(0);
    });

    // Update on swatch change
    document.addEventListener('click', function (e) {
      var el = e.target.closest('.swatch-group-selector, .swatch-view-item, .swatch-image, [swatch-inside]');
      if (el && getMobileRoot().contains(el)) {
        scheduleRewrite(100);
      }
    }, true);
    document.addEventListener('change', function (e) {
      if (getMobileRoot().contains(e.target) && e.target.closest('[option-name], .swatch-single')) {
        scheduleRewrite(50);
      }
    }, true);
    document.addEventListener('variant:change', function () {
      scheduleRewrite(50);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        scheduleRewrite(0);
      }, { once: true });
    } else {
      scheduleRewrite(0);
    }
    rewriteSwatchLabels();
  })();

  /* ── Module 2: leggings/flares length-fit guide ──
     The guide is server-rendered only for the eligible product types, then
     moved directly below whichever length picker is active (native theme or
     asynchronously-rendered Swatch King). Selecting a length opens the inline,
     full-width dialog with only that option's guidance, and positions its arrow
     beneath the selected button. Close and Escape dismiss it. */
  (function () {
    if (window.__pdpLengthFitGuideBound) return;
    window.__pdpLengthFitGuideBound = true;

    var lastTrigger = null;

    function isLengthName(name) {
      var normalized = (name || '').toLowerCase();
      return normalized.indexOf('length') > -1 ||
        normalized.indexOf('inseam') > -1 ||
        normalized.indexOf('leg') > -1;
    }

    function optionName(container) {
      var name = container.getAttribute('option-name') ||
        container.getAttribute('name') ||
        container.getAttribute('data-handle') || '';
      if (!name) {
        var legend = container.querySelector('legend');
        name = legend ? legend.textContent : '';
      }
      return name.replace(/:.*$/, '').trim();
    }

    function findLengthMount(section) {
      var containers = section.querySelectorAll(
        '.pdp-option-length, [option-name], .swatch-single, .variant-input-wrap'
      );
      for (var i = 0; i < containers.length; i++) {
        var container = containers[i];
        if (!container.classList.contains('pdp-option-length') && !isLengthName(optionName(container))) continue;
        return container.matches('.variant-input-wrap')
          ? (container.closest('.variant-wrapper') || container)
          : container;
      }
      return null;
    }

    function placeGuide(section, createGuide) {
      if (!section) return null;
      var guide = section.querySelector('[data-pdp-length-fit-guide]');
      var mount = findLengthMount(section);
      if (!mount) return guide || null;
      if (!guide && createGuide) {
        var template = section.querySelector('template[data-pdp-length-fit-template]');
        if (!template || !template.content || !template.content.firstElementChild) return null;
        guide = template.content.firstElementChild.cloneNode(true);
      }
      if (!guide) return null;
      if (mount.nextElementSibling !== guide) {
        mount.insertAdjacentElement('afterend', guide);
      }
      return guide;
    }

    function setOpen(guide, open) {
      if (!guide) return;
      guide.hidden = !open;
      guide.classList.toggle('is-open', open);
      guide.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    function visualTrigger(target) {
      var trigger = target.closest(
        '.swatch-button, .swatch-group-selector, [role="radio"], input, label, button, .swatch-view-item'
      );
      if (trigger && trigger.matches('label') && trigger.htmlFor) {
        var input = document.getElementById(trigger.htmlFor);
        return { visual: trigger, focus: input || trigger };
      }
      if (trigger && trigger.matches('input')) {
        var inputWrap = trigger.closest('.variant-input');
        var inputLabel = inputWrap ? inputWrap.querySelector('label') : null;
        return { visual: inputLabel || trigger, focus: trigger };
      }
      return { visual: trigger || target, focus: trigger || target };
    }

    function selectedLengthKey(target) {
      var input = target.matches('input') ? target : null;
      var label = target.closest('label');
      if (!input && label && label.htmlFor) input = document.getElementById(label.htmlFor);
      var valueNode = target.closest('[data-value], [orig-value]');
      var value = (input && input.value) ||
        (valueNode && (valueNode.getAttribute('data-value') || valueNode.getAttribute('orig-value'))) ||
        target.getAttribute('data-value') ||
        target.getAttribute('orig-value') ||
        target.getAttribute('aria-label') ||
        target.textContent || '';
      var normalized = value.replace(/\s+/g, ' ').trim().toLowerCase();
      if (normalized.indexOf('short') > -1) return 'short';
      if (normalized.indexOf('tall') > -1) return 'tall';
      if (normalized.indexOf('reg') > -1) return 'regular';
      return null;
    }

    function showContent(guide, key) {
      var active = null;
      guide.querySelectorAll('[data-pdp-length-fit-option]').forEach(function (content) {
        var matches = content.getAttribute('data-pdp-length-fit-option') === key;
        content.hidden = !matches;
        if (matches) active = content;
      });
      if (!active) return false;
      guide.removeAttribute('aria-labelledby');
      guide.setAttribute('aria-label', active.getAttribute('data-pdp-length-fit-label') + ' fit guide');
      guide.setAttribute('data-pdp-length-fit-active', key);
      return true;
    }

    function alignPointer(guide, trigger) {
      if (!guide || !trigger || !trigger.getBoundingClientRect) return;
      var guideRect = guide.getBoundingClientRect();
      var triggerRect = trigger.getBoundingClientRect();
      var pointerX = triggerRect.left + (triggerRect.width / 2) - guideRect.left;
      pointerX = Math.max(18, Math.min(guideRect.width - 18, pointerX));
      guide.style.setProperty('--pdp-length-fit-pointer-x', Math.round(pointerX) + 'px');
      guide.__pdpLengthFitVisualTrigger = trigger;
    }

    function isSelectionTarget(target) {
      return !!target.closest(
        'input[data-variant-input], label, button, [role="radio"], .swatch-view-item, .swatch-group-selector, [swatch-inside]'
      );
    }

    function openFromSelection(target) {
      if (!target || !target.closest || !isSelectionTarget(target)) return;
      var section = target.closest('.product-section');
      if (!section) return;
      var mount = findLengthMount(section);
      if (!mount || !mount.contains(target)) return;
      var guide = placeGuide(section, true);
      if (!guide) return;
      var key = selectedLengthKey(target);
      if (!key || !showContent(guide, key)) return;
      var trigger = visualTrigger(target);
      lastTrigger = trigger.focus;
      setOpen(guide, true);
      alignPointer(guide, trigger.visual);
    }

    /* The pre-selected length control, native radio or Swatch King item. */
    function selectedLengthControl(mount) {
      return mount.querySelector('input[data-variant-input]:checked') ||
        mount.querySelector('.swatch-view-item[aria-checked="true"]') ||
        mount.querySelector('.swatch-selected');
    }

    /* Open the guide for the default-selected length on load — no click
       needed. Skipped once the customer dismisses it (per section). */
    function autoOpen(section) {
      if (section.__pdpLengthFitDismissed) return;
      var existing = section.querySelector('[data-pdp-length-fit-guide]');
      if (existing && !existing.hidden) return;
      var mount = findLengthMount(section);
      if (!mount) return;
      var control = selectedLengthControl(mount);
      if (!control) return;
      var key = selectedLengthKey(control);
      if (!key) return;
      var guide = placeGuide(section, true);
      if (!guide || !showContent(guide, key)) return;
      setOpen(guide, true);
      alignPointer(guide, visualTrigger(control).visual);
    }

    function placeAll() {
      document.querySelectorAll('.product-section').forEach(function (section) {
        placeGuide(section, false);
        autoOpen(section);
      });
    }

    document.addEventListener('click', function (event) {
      var close = event.target.closest('[data-pdp-length-fit-guide-close]');
      if (close) {
        var guide = close.closest('[data-pdp-length-fit-guide]');
        var closedSection = close.closest('.product-section');
        if (closedSection) closedSection.__pdpLengthFitDismissed = true;
        setOpen(guide, false);
        if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
        return;
      }
      openFromSelection(event.target);
    });

    document.addEventListener('change', function (event) {
      openFromSelection(event.target);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var closedGuide = false;
      document.querySelectorAll('[data-pdp-length-fit-guide].is-open').forEach(function (guide) {
        var guideSection = guide.closest('.product-section');
        if (guideSection) guideSection.__pdpLengthFitDismissed = true;
        setOpen(guide, false);
        closedGuide = true;
      });
      if (closedGuide && lastTrigger && lastTrigger.focus) lastTrigger.focus();
    });

    document.addEventListener('dfyne:sibling-product:loaded', placeAll);
    document.addEventListener('shopify:section:load', placeAll);
    window.addEventListener('resize', function () {
      document.querySelectorAll('[data-pdp-length-fit-guide].is-open').forEach(function (guide) {
        alignPointer(guide, guide.__pdpLengthFitVisualTrigger);
      });
    }, { passive: true });
    function realignOpen() {
      document.querySelectorAll('[data-pdp-length-fit-guide].is-open').forEach(function (guide) {
        alignPointer(guide, guide.__pdpLengthFitVisualTrigger);
      });
    }

    function init() {
      placeAll();
      // Swatch King renders its controls asynchronously — retry the
      // auto-open briefly until its selected length exists. Idempotent:
      // placeAll no-ops once the guide is open or dismissed.
      [400, 1200, 3000].forEach(function (delay) {
        setTimeout(placeAll, delay);
      });
      // Raleway swapping in changes pill widths — re-aim the arrow.
      window.addEventListener('load', realignOpen, { once: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  })();

  /* ── Module 3: size label engine ──
     Size label shows "SIZE: <size> | <length>" using the size code (e.g. XS).
     The length option has no label row — its value is appended to the size label. */
  (function () {
    if (window.__pdpSizeLabelBound) return;
    window.__pdpSizeLabelBound = true;
    function handleOf(fs) { return (fs.getAttribute('data-handle') || '').toLowerCase(); }
    function isLength(h) { return h.indexOf('length') > -1 || h.indexOf('inseam') > -1 || h.indexOf('leg') > -1; }
    function update(section) {
      var sizeVal = '', lengthVal = '', valEl = null;
      section.querySelectorAll('.variant-input-wrap').forEach(function (fs) {
        var checked = fs.querySelector('input[data-variant-input]:checked');
        if (!checked) return;
        var h = handleOf(fs);
        if (h.indexOf('size') > -1) {
          sizeVal = checked.value;
          var wrap = fs.closest('.variant-wrapper--labeled');
          if (wrap) { valEl = wrap.querySelector('[data-variant-selected-value]'); }
        } else if (isLength(h)) {
          lengthVal = checked.value;
        }
      });
      if (valEl) {
        valEl.textContent = sizeVal + (lengthVal ? ' | ' + lengthVal : '');
      }
    }
    document.addEventListener('change', function (e) {
      var input = e.target.closest('input[data-variant-input]');
      if (!input) return;
      var section = input.closest('.product-section');
      if (section) { update(section); }
    });
    document.querySelectorAll('.product-section').forEach(update);
  })();

  /* ── Module 4: fit badge ("True to Size") and linked payment tooltip ──
     Desktop (hover-capable): reveal on hover / :focus-visible; mouse clicks do
     nothing, keyboard activation toggles. Touch: tap toggles, tap elsewhere or
     Escape closes. aria-expanded mirrors the .is-open state. */
  (function () {
    if (window.__pdpFitBadgeBound) return;
    window.__pdpFitBadgeBound = true;
    function hoverCapable() { return window.matchMedia && window.matchMedia('(hover: hover)').matches; }
    function triggerOf(badge) { return badge.querySelector('[data-pdp-fit-trigger]') || badge; }
    function setOpen(badge, open) {
      badge.classList.toggle('is-open', open);
      if (open) badge.classList.remove('is-dismissed');
      triggerOf(badge).setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    document.addEventListener('click', function (e) {
      var badge = e.target.closest('[data-pdp-fit-badge]');
      if (badge && e.target.closest('a')) return;
      var keyboard = false;
      try { keyboard = !!(badge && triggerOf(badge).matches(':focus-visible')); } catch (err) {}
      document.querySelectorAll('[data-pdp-fit-badge].is-open').forEach(function (b) {
        if (b !== badge) setOpen(b, false);
      });
      if (hoverCapable() && !keyboard) return;
      if (badge) { e.preventDefault(); setOpen(badge, !badge.classList.contains('is-open')); }
    });
    // Linked tooltips keep the link reachable by keyboard and can dismiss while hovered.
    document.addEventListener('focusout', function (e) {
      var badge = e.target.closest('.pdp-fit-badge--linked');
      if (badge && !badge.contains(e.relatedTarget)) setOpen(badge, false);
    });
    ['pointerover', 'focusin'].forEach(function (eventName) {
      document.addEventListener(eventName, function (e) {
        var badge = e.target.closest('.pdp-fit-badge--linked');
        if (!badge || badge.contains(e.relatedTarget)) return;
        badge.classList.remove('is-dismissed');
        if (eventName === 'pointerover' && e.pointerType === 'mouse') {
          clearTimeout(badge._pdpFitCloseTimer);
          setOpen(badge, true);
        }
      });
    });
    document.addEventListener('pointerout', function (e) {
      var badge = e.target.closest('.pdp-fit-badge--linked');
      if (!badge || badge.contains(e.relatedTarget) || e.pointerType !== 'mouse') return;
      // Allow a diagonal move from the small icon onto the wider terms link.
      badge._pdpFitCloseTimer = setTimeout(function () { setOpen(badge, false); }, 200);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('[data-pdp-fit-badge].is-open').forEach(function (b) { setOpen(b, false); });
        document.querySelectorAll('.pdp-fit-badge--linked').forEach(function (badge) {
          if (badge.matches(':hover, :focus-within')) {
            if (badge.contains(document.activeElement)) triggerOf(badge).focus();
            badge.classList.add('is-dismissed');
          }
        });
      }
    });
  })();

  /* ── Module 5: add-to-cart availability guard ──
     Sold-out sizes stay clickable, but the add-to-cart button reflects the
     selected variant's availability immediately — this runs before theme.js
     in defer order, so a sold-out variant can never be added in the window
     before the theme's variant engine binds. (The server also renders the
     button disabled when the initial variant is unavailable, and Shopify
     rejects unavailable variants at /cart/add.) */
  (function () {
    if (window.__pdpCartGuardBound) return;
    window.__pdpCartGuardBound = true;
    function soldOutLabel() {
      return (window.theme && theme.strings && theme.strings.soldOut) || 'Sold Out';
    }
    function evaluate(section) {
      if (!section) return;
      var jsonEl = section.querySelector('[data-variant-json]');
      var btn = section.querySelector('[data-add-to-cart]');
      var txt = section.querySelector('[data-add-to-cart-text]');
      if (!jsonEl || !btn) return;
      var variants;
      try { variants = JSON.parse(jsonEl.value || jsonEl.textContent || '[]'); } catch (e) { return; }
      var sel = {};
      section.querySelectorAll('input[data-variant-input]:checked').forEach(function (i) {
        sel[(i.getAttribute('data-index') || '').replace('option', '')] = i.value;
      });
      var found = null;
      for (var k = 0; k < variants.length; k++) {
        var v = variants[k];
        if ((!sel['1'] || v.option1 === sel['1']) && (!sel['2'] || v.option2 === sel['2']) && (!sel['3'] || v.option3 === sel['3'])) { found = v; break; }
      }
      var available = !!(found && found.available);
      if (available) {
        btn.disabled = false;
        btn.classList.remove('disabled');
        if (txt && txt.getAttribute('data-default-text')) { txt.textContent = txt.getAttribute('data-default-text'); }
      } else {
        btn.disabled = true;
        btn.classList.add('disabled');
        if (txt) { txt.textContent = soldOutLabel(); }
      }
    }
    document.addEventListener('change', function (e) {
      var input = e.target.closest('input[data-variant-input]');
      if (input) { evaluate(input.closest('.product-section')); }
    }, true);
    function initAll() { document.querySelectorAll('.product-section').forEach(evaluate); }
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initAll); }
    else { initAll(); }
  })();

  /* ── Module 6: blur-up placeholder re-armer ──
     The above-fold desktop slides ship blurred placeholders
     (image-wrap--blur-up, veil in CSS). The PRIMARY binder is inline in
     product-images.liquid — paint-critical, must run at parse. This
     module only re-arms swapped-in sections (sibling swap / section
     re-render), where injected inline scripts never execute. The
     __blurUpArmed flag keeps the two binders from double-binding.
     Fail-open: a veil always clears within 8s. */
  (function () {
    if (window.__pdpBlurUpBound) return;
    window.__pdpBlurUpBound = true;
    function arm(wrap) {
      if (wrap.__blurUpArmed) return;
      wrap.__blurUpArmed = true;
      var img = wrap.querySelector('img');
      var done = function () { wrap.classList.add('blur-up--done'); };
      if (!img) { done(); return; }
      if (img.complete && img.naturalWidth > 0) { done(); return; }
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, 8000);
    }
    function initAll() {
      document.querySelectorAll('.image-wrap--blur-up:not(.blur-up--done)').forEach(arm);
    }
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', initAll); }
    else { initAll(); }
    document.addEventListener('dfyne:sibling-product:loaded', initAll);
    document.addEventListener('shopify:section:load', initAll);
  })();

  /* ── Module 7: desktop sticky-offset engine ──
     Sets --pdp-desktop-meta-sticky-top so the meta column parks with its
     bottom in view when the gallery is taller than the viewport. */
  (function () {
    if (window.__pdpStickyInit) return;
    window.__pdpStickyInit = true;

    var desktopQuery = window.matchMedia('(min-width: 769px)');
    var activeObserver = null;
    var raf = null;
    var current = null;

    function initStickyOffset(root) {
      if (!root) return;

      var meta = root.querySelector('.product-single__meta');
      if (!meta) return;

      var detailsCol = root.querySelector('.product-single__details');
      var leftCol = root.querySelector('.product-single__sticky');
      var mediaCol = leftCol ? leftCol.querySelector('.product__main-photos') : null;
      var frame = root.querySelector('.page-content--product > .page-width > .grid');

      function setStickyOffset() {
        if (raf) window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(function () {
          if (!desktopQuery.matches) {
            root.style.removeProperty('--pdp-desktop-meta-sticky-top');
            if (detailsCol) detailsCol.classList.remove('pdp-desktop-sticky-enabled');
            return;
          }

          var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          var mediaHeight = mediaCol ? mediaCol.offsetHeight : 0;
          var detailsHeight = meta.offsetHeight;
          var canStick = detailsCol && mediaHeight > viewportHeight + 1 && mediaHeight > detailsHeight + 1;

          if (!canStick) {
            root.style.removeProperty('--pdp-desktop-meta-sticky-top');
            if (detailsCol) detailsCol.classList.remove('pdp-desktop-sticky-enabled');
            return;
          }

          var stickyTop = Math.min(0, viewportHeight - detailsHeight - 16);
          root.style.setProperty('--pdp-desktop-meta-sticky-top', stickyTop + 'px');
          detailsCol.classList.add('pdp-desktop-sticky-enabled');
        });
      }

      current = setStickyOffset;

      // Re-create the observer against the current section's elements; the
      // previous section's nodes are detached after a swap.
      if (activeObserver) activeObserver.disconnect();
      activeObserver = null;
      if (window.ResizeObserver) {
        activeObserver = new ResizeObserver(setStickyOffset);
        activeObserver.observe(meta);
        if (detailsCol) activeObserver.observe(detailsCol);
        if (mediaCol) activeObserver.observe(mediaCol);
        if (frame) activeObserver.observe(frame);
      }

      setStickyOffset();
    }

    function run() { if (current) current(); }

    window.addEventListener('load', run);
    window.addEventListener('resize', run, { passive: true });
    document.addEventListener('variant:change', run);
    document.addEventListener('dfyne:sibling-product:loaded', function (e) {
      var section = (e.detail && e.detail.productSection) || getProductSection();
      initStickyOffset(section);
    });

    initStickyOffset(getProductSection());
  })();

  /* ── Module 8: review-stars scroll offset ──
     Clicking the star rating in the PDP header jumps to the Customer Reviews
     section, but Judge.me does that with window.scrollTo, which ignores
     scroll-margin-top and scroll-padding-top (both verified to have no effect).
     It also takes no account of the fixed .site-header, so the landing put the
     section's top divider and the "Customer Reviews" heading behind the header —
     worse since the new nav made the header taller.

     So we take the click in the capture phase, stop it reaching Judge.me's own
     handler, and scroll ourselves: the section's top divider ends up just below
     the header. Lives here rather than in theme.js because theme.js is gated
     behind the PDP hero gate (up to 4s), whereas this file is only deferred. */
  (function () {
    if (window.__pdpReviewScrollBound) return;
    window.__pdpReviewScrollBound = true;

    var GAP_BELOW_HEADER = 16;

    function reviewsSection() {
      var widget = document.getElementById('judgeme_product_reviews')
        || document.querySelector('.jdgm-rev-widg');
      if (!widget) return null;
      /* The divider is a ::before on the section's .page-width, so that element's
         top IS the divider's top. */
      return widget.closest('.page-width') || widget;
    }

    function headerOffset() {
      var header = document.querySelector('.site-header');
      if (!header) return 0;
      /* Measured at click time. On mobile the at-rest and stuck heights are the
         same, so this is exact; on desktop the stuck header is never taller than
         the at-rest one, so erring here only ever leaves a slightly larger gap. */
      return header.offsetHeight || 0;
    }

    function isStarBadge(node) {
      if (!node || !node.closest) return false;
      /* Only the rating in the PDP header — not the stars inside the review list,
         which are decorative, and not review-card stars elsewhere on the page. */
      return !!node.closest('.pdp-mobile-header__reviews .jdgm-prev-badge, [data-pdp-mobile-reviews] .jdgm-prev-badge');
    }

    document.addEventListener('click', function (evt) {
      if (!isStarBadge(evt.target)) return;

      var target = reviewsSection();
      if (!target) return;

      evt.preventDefault();
      evt.stopPropagation();

      var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset() - GAP_BELOW_HEADER;

      window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'smooth' });
    }, true);
  })();
})();
